import { useState, useRef } from 'react';

// Renders comment body with @[Name] mentions highlighted in a pill style
export function CommentBody({ body, style }) {
  if (!body) return null;
  // Match @[Any Name Here] — unambiguous bracket format
  const parts = body.split(/(@\[[^\]]+\])/g);
  return (
    <p style={{ margin: 0, fontSize: '0.85rem', color: '#374151', lineHeight: 1.7, ...style }}>
      {parts.map((p, i) =>
        p.startsWith('@[') && p.endsWith(']')
          ? (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'center',
              background: '#FFF7ED', color: '#EA580C',
              fontWeight: 700, fontSize: '0.8rem',
              borderRadius: '999px', padding: '0 0.45rem',
              border: '1px solid #FED7AA', margin: '0 1px',
              lineHeight: 1.6,
            }}>
              @{p.slice(2, -1)}
            </span>
          )
          : p
      )}
    </p>
  );
}

// Mention-aware textarea — inserts @[Full Name] on selection
export function MentionInput({ value, onChange, members = [], style, placeholder, rows = 2 }) {
  const [query, setQuery]       = useState('');
  const [showDrop, setShowDrop] = useState(false);
  const [atPos, setAtPos]       = useState(-1);
  const taRef = useRef(null);

  const handleChange = (e) => {
    const val  = e.target.value;
    onChange(val);
    const cur    = e.target.selectionStart;
    const before = val.slice(0, cur);
    // Detect @word... after the last @
    const match  = before.match(/@([^@\]]*)$/);
    if (match && !match[1].includes('[')) {
      setQuery(match[1]);
      setAtPos(before.lastIndexOf('@'));
      setShowDrop(true);
    } else {
      setShowDrop(false);
      setAtPos(-1);
    }
  };

  const insertMention = (name) => {
    if (atPos < 0) return;
    const cur    = taRef.current?.selectionStart ?? value.length;
    const after  = value.slice(cur);
    const before = value.slice(0, atPos);
    // Insert @[Full Name]  — brackets make it unambiguous
    const token  = `@[${name}] `;
    const newVal = `${before}${token}${after}`;
    onChange(newVal);
    setShowDrop(false);
    setAtPos(-1);
    setTimeout(() => {
      if (taRef.current) {
        const pos = before.length + token.length;
        taRef.current.setSelectionRange(pos, pos);
        taRef.current.focus();
      }
    }, 0);
  };

  const filtered = query
    ? members.filter(m => (m.employee_name || m.name || '').toLowerCase().includes(query.toLowerCase()))
    : members;

  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <textarea
        ref={taRef}
        value={value}
        onChange={handleChange}
        onKeyDown={(e) => { if (e.key === 'Escape') setShowDrop(false); }}
        rows={rows}
        placeholder={placeholder || 'Add a comment… type @ to mention someone'}
        style={{ width: '100%', boxSizing: 'border-box', resize: 'none', ...style }}
      />
      {showDrop && filtered.length > 0 && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 0, zIndex: 9999,
          background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8,
          boxShadow: '0 4px 14px rgba(0,0,0,0.12)', minWidth: 220,
          maxHeight: 180, overflowY: 'auto',
        }}>
          {filtered.slice(0, 8).map((m, i) => {
            const name = m.employee_name || m.name || '';
            const code = m.emp_code || '';
            return (
              <div
                key={m.employee_id || m.id || i}
                onMouseDown={(e) => { e.preventDefault(); insertMention(name); }}
                style={{
                  padding: '0.5rem 0.875rem', cursor: 'pointer',
                  fontSize: '0.82rem', color: '#111827',
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#FFF7ED'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: '#FED7AA', color: '#EA580C',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '0.75rem', flexShrink: 0,
                }}>
                  {name.charAt(0).toUpperCase()}
                </span>
                <span style={{ fontWeight: 600 }}>{name}</span>
                {code && <span style={{ color: '#9CA3AF', fontSize: '0.72rem', marginLeft: 'auto' }}>{code}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
